import {
  PVP_TEAM_JOIN_ROUTE,
  PVP_TEAM_LAUNCH_ROUTE,
  PVP_TEAM_LEAVE_ROUTE,
  PVP_TEAM_LOBBY_ROUTE,
  PVP_TEAM_SOCKET_ROUTE,
  PVP_TEAM_TICKET_ROUTE,
  type PvpTeamLobby,
  type PvpTeamTicketResponse,
  pvpTeamLobbySchema,
  pvpTeamTicketResponseSchema,
} from '@kenocyber/shared';
import { z } from 'zod';
import { API_BASE_URL } from '../auth/client';
import { pvpGet, pvpPost } from './pvp';

/**
 * The team lobby's six authenticated requests, and the URL of its socket.
 *
 * ## Why this is a second module and not four more functions in `pvp.ts`
 *
 * The two modes share a transport and share nothing else. A duel's routes are
 * keyed by a MATCH the caller names and answer with a SEAT; a team lobby's
 * routes name nothing at all - the chair, the team, the slot, the match and the
 * launch are read from the session - and answer with a roster. Putting both in
 * one file would put a `matchId` argument next to functions that must never have
 * one, and the dangerous version of that mistake is the quiet one: a request
 * that carried a chair somebody could choose.
 *
 * `pvpPost` and `pvpGet` ARE shared, because "what a refusal means" is one
 * decision and two copies of it eventually disagree.
 *
 * ## Nothing here decides a team
 *
 * The only thing a client ever says is a CODE. Which team a code opens is a
 * column beside it on the server; there is no team field in any request on this
 * page, and no response shape here has a place to put one that the caller chose.
 *
 * ## The ticket never lands anywhere it could be read
 *
 * {@link mintPvpTeamSocketTicket} answers into `PvpTeamConnection`, which puts
 * the capability straight into a socket URL and forgets it. It is never React
 * state, never a prop, never an attribute, never stored and never logged.
 */

/**
 * What `GET /api/pvp/team/lobby` answers.
 *
 * Declared here rather than in `packages/shared`: the envelope is this route's
 * own answer shape and nothing on the wire depends on it, so widening the shared
 * protocol module to hold it would be a protocol change for a client's
 * convenience. The lobby inside it is parsed against the SHARED schema, which is
 * the part both sides have to agree on.
 */
const teamLobbyReadSchema = z.object({ lobby: pvpTeamLobbySchema.nullable() });

/** What `POST /api/pvp/team/leave` answers. Whether the room died with it. */
const teamLeaveSchema = z.object({ cancelled: z.boolean() });

/**
 * Opens a private team lobby and takes its first chair.
 *
 * The body is EMPTY, and the schema on the other side is strict: there is no
 * mode, no ruleset, no team and no slot to send. The creator's own chair - `a1`,
 * always - and both invite codes come back in the answer.
 */
export async function createPvpTeamLobby(): Promise<PvpTeamLobby> {
  return pvpPost(PVP_TEAM_LOBBY_ROUTE, {}, (value) => pvpTeamLobbySchema.parse(value));
}

/**
 * Takes a chair with a code somebody handed out.
 *
 * The code is the ONLY thing sent, and it is what decides the team: a client
 * that wanted the other side would have to be given the other code by the person
 * who opened the room. There is no field here through which a team could be
 * asked for, and the server would refuse one if there were.
 */
export async function joinPvpTeamLobby(code: string): Promise<PvpTeamLobby> {
  return pvpPost(PVP_TEAM_JOIN_ROUTE, { code }, (value) => pvpTeamLobbySchema.parse(value));
}

/**
 * The lobby this account is sitting in, or `null`.
 *
 * Scoped by the session and by nothing else - there is no match id to send, and
 * holding a code grants no read at all. This is how a reloaded tab finds the
 * room it was in, and like the duel's discovery read it authorises nothing: the
 * ticket route re-checks the chair, and the room re-checks the ticket.
 */
export async function readPvpTeamLobby(): Promise<PvpTeamLobby | null> {
  const answer = await pvpGet(PVP_TEAM_LOBBY_ROUTE, (value) => teamLobbyReadSchema.parse(value));
  return answer.lobby;
}

/** Gives the chair up. Cancels the room when the caller was the one who opened it. */
export async function leavePvpTeamLobby(): Promise<{ readonly cancelled: boolean }> {
  return pvpPost(PVP_TEAM_LEAVE_ROUTE, {}, (value) => teamLeaveSchema.parse(value));
}

/**
 * Stops the roster changing, so capabilities can exist at all.
 *
 * Only the account that opened the lobby may do it, and that is the SERVER's
 * rule - this function is the same request from anybody, and the refusal is the
 * server's answer rather than a check made here.
 */
export async function launchPvpTeamLobby(): Promise<PvpTeamLobby> {
  return pvpPost(PVP_TEAM_LAUNCH_ROUTE, {}, (value) => pvpTeamLobbySchema.parse(value));
}

/**
 * One capability, for one socket, for the caller's own frozen chair.
 *
 * The body is empty: the chair comes from the database keyed by the session, so
 * asking for somebody else's ticket is not refused - it has no representation.
 * Single use, thirty seconds, and a fresh one for every connection and every
 * reconnection.
 */
export async function mintPvpTeamSocketTicket(): Promise<PvpTeamTicketResponse> {
  return pvpPost(PVP_TEAM_TICKET_ROUTE, {}, (value) => pvpTeamTicketResponseSchema.parse(value));
}

/**
 * Where the team socket lives, with the capability in the query string.
 *
 * A different path from the duel's, and deliberately so: the two capabilities
 * are signed under different domain labels and are decoded by different modules,
 * so a version 4 ticket cannot be verified as a version 5 one. One path with a
 * decoder chosen at runtime would be one handler making the dangerous choice
 * quietly.
 */
export function pvpTeamSocketUrl(ticket: string): string {
  const base = new URL(API_BASE_URL);
  base.protocol = base.protocol === 'https:' ? 'wss:' : 'ws:';
  base.pathname = PVP_TEAM_SOCKET_ROUTE;
  base.search = `?ticket=${encodeURIComponent(ticket)}`;
  return base.toString();
}
